import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoEye } from "react-icons/io5";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { useCategoryProducts } from "./useHook";
import Loader, { ImageWithFallback } from "../../../Utils/loader";
import Swal from "sweetalert2";

function CategoryView() {
  const navigate = useNavigate();
  const { getCategoryProducts, getCategoryProductsWithPagination, searchCategoryProducts } = useCategoryProducts();
  const [products, setCategoryProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const { id } = location.state || {};

  const [offset, setOffset] = useState(0);
  const [limit] = useState(10); // Items per page
  const [total, setTotal] = useState(0);
  const [nameQuery, setNameQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchCategoryProducts = async (categoryId, currentOffset = offset, showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }
    try {
      await getCategoryProductsWithPagination(categoryId, setCategoryProducts, setTotal, currentOffset, limit, nameQuery, statusFilter);
    } catch (error) {
      console.error("Error fetching category products:", error);
      Swal.fire("Error", "Failed to fetch category products", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setOffset(0);
      const response = await searchCategoryProducts(id, nameQuery, statusFilter, 0, limit);
      setCategoryProducts(response.products);
      const totalRecords = response.pagination?.totalRecords || response.products.length;
      setTotal(totalRecords);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setNameQuery("");
    setStatusFilter("");
    setOffset(0);
    fetchCategoryProducts(id, 0);
  };

  useEffect(() => {
    if (id) {
      fetchCategoryProducts(id, 0, true);
    }
  }, [id, offset]);

  const handlePageChange = (newOffset) => {
    setIsLoading(true);
    setOffset(newOffset);
    fetchCategoryProducts(id, newOffset, false);
  };

  const handlePageView = (productId) => {
    navigate("/category/view/products", { state: { productId } });
  };

  if (isLoading) {
    return <Loader message="Loading category products..." />;
  }

  return (
    <div>
      <div className="bg-gray-100 p-4 flex justify-between">
        <h2 className="text-2xl font-bold">Category Products</h2>
      </div>

      <div className="mt-4">
        <div className="bg-white p-4 rounded-md shadow-md mb-6 flex gap-4 mt-4">
          <input
            type="text"
            className="w-[40%] flex-grow px-4 py-2 border rounded-lg"
            placeholder="Search by product name"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
          />
          <select
            className="w-[20%] px-4 py-2 border rounded-lg"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="" className="opacity-25">
              Select Status
            </option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button
            className="w-full sm:w-[45%] md:w-60 bg-blue-500 text-white px-4 py-2 rounded-md"
            onClick={handleSearch}
          >
            Search
          </button>
          <button
            className="w-full sm:w-[45%] md:w-60 bg-primary-dark text-white px-4 py-2 rounded-md"
            onClick={handleReset}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="overflow-x-auto mt-6">
        <table className="min-w-full table-auto bg-white shadow-md rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Sr. No.
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Image
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Name
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Price
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Status
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {products.length > 0 ? (
              products.map((product, index) => (
                <tr
                  key={product.id}
                  className={`${index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                >
                  <td className="px-6 py-4 text-sm text-gray-700">{offset + index + 1}</td>
                  <td className="px-6 py-4">
                    <ImageWithFallback
                      src={product?.image}
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded-full shadow-lg"
                    />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{product.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{product.currency} {product.price}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-sm ${product.status
                        ? "bg-green-200 text-green-800"
                        : "bg-red-200 text-red-800"
                        }`}
                    >
                      {product.status ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      className="bg-blue-500 text-white py-2 px-4 rounded-lg shadow-md flex items-center justify-center"
                      onClick={() => handlePageView(product.id)}
                    >
                      <IoEye />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="7"
                  className="text-center text-gray-500 py-6 text-sm bg-white"
                >
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}
    </div>
  );
}

export default CategoryView;